import React, { useEffect, useState } from 'react';
import axios from 'axios'
import {
  Card, CardContent, FormControlLabel, Switch, InputAdornment, IconButton, TextField, InputBase, Box, Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { parseISO8601Duration } from '../../utils/playlistUtils';

export default function SearchCard({ setKeySearch, setVideosSearchResponse, keySearch, onIsKaraokeOnly, isKaraokeOnly, setSnackMessage, userSettings, selectedApiKey, setSelectedApiKey }) {
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const getYoutubeVideos = async (searchQuery, maxResults = 15, apiKeyByParameter = '') => {
    try {
      const finalApiKey = apiKeyByParameter || selectedApiKey || process.env.YOUTUBE_API_KEY || process.env.REACT_APP_DEFAULT_YOUTUBE_API_KEY;
      const apiKey = finalApiKey;
      const apiUrl = process.env.VITE_YOUTUBE_API_URL || process.env.REACT_APP_YOUTUBE_API_URL || 'https://www.googleapis.com/youtube/v3/search';
      //all parametters in: https://developers.google.com/youtube/v3/docs/search/list?hl=es-419
      return await axios.get(apiUrl, {
        params: {
          q: isKaraokeOnly ? searchQuery + ' karaoke' : searchQuery,
          part: 'snippet',
          key: apiKey,
          maxResults: maxResults,
          type: 'video',
          videoEmbeddable: true,
        },
      }).then(async (r) => {
        const rawItems = r.data.items || [];
        let enrichedItems = [...rawItems];

        // Leer caché local de duraciones
        let durationCache = {};
        try {
          const cachedStr = localStorage.getItem('garaoke_duration_cache');
          if (cachedStr) durationCache = JSON.parse(cachedStr);
        } catch (e) {}

        const missingVideoIds = [];
        enrichedItems = enrichedItems.map((item) => {
          const vidId = item.id?.videoId || item.id;
          if (vidId && durationCache[vidId]) {
            return { ...item, duration: durationCache[vidId] };
          }
          if (vidId) missingVideoIds.push(vidId);
          return item;
        });

        // Si hay videos sin duración en caché, consultar en lote a la API de YouTube videos?part=contentDetails
        if (missingVideoIds.length > 0 && apiKey) {
          try {
            const videosDetailsUrl = (process.env.VITE_YOUTUBE_API_URL || 'https://www.googleapis.com/youtube/v3/search')
              .replace(/\/search\/?$/, '/videos');
            const detailsRes = await axios.get(videosDetailsUrl, {
              params: {
                part: 'contentDetails',
                id: missingVideoIds.join(','),
                key: apiKey,
              }
            });

            if (detailsRes?.data?.items) {
              detailsRes.data.items.forEach((detailItem) => {
                const dId = detailItem.id;
                const isoDur = detailItem.contentDetails?.duration;
                const durSec = parseISO8601Duration(isoDur);
                if (durSec > 0) {
                  durationCache[dId] = durSec;
                }
              });

              try {
                localStorage.setItem('garaoke_duration_cache', JSON.stringify(durationCache));
              } catch (e) {}

              enrichedItems = enrichedItems.map((item) => {
                const vidId = item.id?.videoId || item.id;
                if (vidId && durationCache[vidId]) {
                  return { ...item, duration: durationCache[vidId] };
                }
                return item;
              });
            }
          } catch (detailsErr) {
            console.warn('No se pudo obtener detalles de duración en lote de YouTube:', detailsErr);
          }
        }

        setVideosSearchResponse(enrichedItems);
        setError('');

        setSelectedApiKey(selectedApiKey);
        return true;
      }).catch((e) => {
        setVideosSearchResponse([]);
        setSelectedApiKey(selectedApiKey)

        switch (e.code) {
          case 'ERR_BAD_REQUEST':
            let actionVerb = 'establecer'
            if (userSettings.configs.apiKeys.filter((apiKey) => apiKey).length > 0) actionVerb = 'cambiar'
            setError(`Es probable que tengas que ${actionVerb} tus API Key para que el buscador basado en Youtube funcione. Ve a la sección de configuración, pestaña 'API Keys de Youtube' para realizar esta acción.`);
            break;
          default:
            setError('Error desconocido: ' + e.message);
        }
        console.error(error)
        return false
      });
    } catch (error) {
      setVideosSearchResponse([]);
      console.error('error=', error)
      setError('Error al obtener videos de YouTube:' + error.message);
      setSnackMessage({ text: 'Error al obtener videos de YouTube:' + error.message, type: 'error', duration: 10000 })
      return false
      //throw error;
    }
  };

  const [searchTerm, setSearchTerm] = useState(keySearch || '');

  const getLastApiKeyWorkIndex = (selectedApiKey, apiKeys) => {
    return apiKeys.indexOf(selectedApiKey)
  }

  const handlerSearch = (termToSearch) => {
    const inputEl = document.getElementById('inputSearch');
    const search = termToSearch !== undefined ? termToSearch : (searchTerm || (inputEl ? inputEl.value : ''));
    setKeySearch(search)
    let apiKeyWork = false;
    const apiKeys = userSettings.configs.apiKeys.filter((apiKey) => apiKey)
    const lastApiKeyWorkIndex = selectedApiKey === process.env.REACT_APP_DEFAULT_YOUTUBE_API_KEY ? -1 : getLastApiKeyWorkIndex(selectedApiKey, apiKeys)
    let tryCount = lastApiKeyWorkIndex === -1 ? 0 : lastApiKeyWorkIndex
    let apisCount = apiKeys.length
    let isFirstLoop = true

    if (!apiKeyWork && tryCount === 0 && apisCount === 0) {
      //dont have apiKeys configured
      setSelectedApiKey(process.env.REACT_APP_DEFAULT_YOUTUBE_API_KEY)
      getYoutubeVideos(search, process.env.REACT_APP_YOUTUBE_MAX_RESULTS, process.env.REACT_APP_DEFAULT_YOUTUBE_API_KEY).then((apiKeyWork2) => {
        if (!apiKeyWork2) {
          setSnackMessage({ text: error, type: 'error', duration: 10000 })
        }
      }
      )
    } else {
      while (!apiKeyWork && tryCount < apisCount) {
        setSelectedApiKey(apiKeys[tryCount])
        getYoutubeVideos(search, process.env.REACT_APP_YOUTUBE_MAX_RESULTS, apiKeys[tryCount]).then(apiKeyWork => {
          if (!apiKeyWork) {
            setSelectedApiKey(process.env.REACT_APP_DEFAULT_YOUTUBE_API_KEY)
            //intentar con la apikey por defecto
            getYoutubeVideos(search, process.env.REACT_APP_YOUTUBE_MAX_RESULTS, process.env.REACT_APP_DEFAULT_YOUTUBE_API_KEY).then((apiKeyWork2) => {
              if (!apiKeyWork2) {
                setSnackMessage({ text: error, type: 'error', duration: 10000 })
              }
            }
            )
          }
        }
        )
        tryCount++;
        if (lastApiKeyWorkIndex !== -1 && tryCount === apisCount && isFirstLoop) {
          tryCount = 0;
          apisCount = lastApiKeyWorkIndex;
          isFirstLoop = false;
        }
      }
    }
  };

  const handlerIsKaraokeOnly = (checked) => {
    onIsKaraokeOnly(checked)
  };

  useEffect(() => {
    if (keySearch !== undefined) {
      setSearchTerm(keySearch);
      const inputEl = document.getElementById('inputSearch');
      if (inputEl) inputEl.value = keySearch;
    }
  }, [keySearch]);

  return (
    <Card elevation={3} style={{ padding: '10px 12px', margin: 0 }}>
      <CardContent style={{ padding: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.14)',
            borderRadius: '12px',
            p: '3px 4px 5px 14px',
            transition: 'all 0.25s ease',
            boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.2)',
            '&:hover': {
              borderColor: 'rgba(255, 45, 155, 0.45)',
              background: 'rgba(255, 255, 255, 0.07)',
            },
            '&:focus-within': {
              borderColor: '#ff2d9b',
              background: 'rgba(255, 255, 255, 0.08)',
              boxShadow: '0 0 18px rgba(255, 45, 155, 0.35), inset 0 1px 3px rgba(0, 0, 0, 0.2)',
            }
          }}
        >
          <InputBase
            id="inputSearch"
            placeholder={t('search.placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handlerSearch(searchTerm);
              }
            }}
            sx={{
              flex: 1,
              minWidth: 0,
              color: '#ffffff',
              fontSize: '0.88rem',
              '& input::placeholder': {
                color: 'rgba(255, 255, 255, 0.45)',
                opacity: 1,
              }
            }}
          />

          <Tooltip title={t('search.searchButton')}>
            <IconButton
              onClick={() => handlerSearch(searchTerm)}
              size="small"
              aria-label={t('search.searchButton')}
              sx={{
                flexShrink: 0,
                width: 28,
                height: 28,
                p: 0,
                color: 'rgba(255, 255, 255, 0.85)',
                background: 'linear-gradient(135deg, rgba(255, 45, 155, 0.25) 0%, rgba(180, 77, 255, 0.22) 100%)',
                borderRadius: '30%',
                ml: 1,
                border: '1px solid rgba(255, 45, 155, 0.35)',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.06)',
                  color: '#ffffff',
                  background: 'linear-gradient(135deg, rgba(255, 45, 155, 0.45) 0%, rgba(180, 77, 255, 0.4) 100%)',
                  borderColor: 'rgba(255, 45, 155, 0.6)',
                  boxShadow: '0 0 12px rgba(255, 45, 155, 0.35)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                }
              }}
            >
              <Search size={16} strokeWidth={2.2} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.8, px: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <MusicNoteIcon sx={{ fontSize: 16, color: isKaraokeOnly ? '#ff2d9b' : 'rgba(255, 255, 255, 0.4)', transition: 'color 0.2s ease' }} />
            <span style={{ fontSize: '0.8rem', fontWeight: 500, color: isKaraokeOnly ? '#ffffff' : 'rgba(255, 255, 255, 0.65)' }}>
              {t('search.karaokeOnly')}
            </span>
          </Box>
          <Switch
            size="small"
            checked={isKaraokeOnly}
            onChange={(event) => handlerIsKaraokeOnly(event.target.checked)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: '#ff2d9b',
                '&:hover': {
                  backgroundColor: 'rgba(255, 45, 155, 0.15)',
                },
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#b44dff',
                opacity: 0.85
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
}